/* src/lib/indexedImage.ts ----------------------------------------------- */
import { openDB } from 'idb';
import { IndexedBlobLike, toReadableBlob, toStorableBlobPayload } from '@/utils/indexedBlob';

/* ---------------------------------------------------------------------- */
/*  Constants                                                             */
/* ---------------------------------------------------------------------- */
import { IMAGE_DB_NAME, IMAGE_DB_VERSION, PHOTOSHOOT_TRANSFERS_STORE } from '@/utils/indexedPhotoshoot';

const DB_NAME    = IMAGE_DB_NAME;
const DB_VERSION = IMAGE_DB_VERSION;
const DB_STORE   = 'images';

/* ---------------------------------------------------------------------- */
/*  Types                                                                 */
/* ---------------------------------------------------------------------- */
/** Single-slot bridge flow uses `'latest'`; AI open-in-editor uses a unique transfer id. */
export interface IndexedImageRecord {
  id: string;
  blob: IndexedBlobLike;
  blobType?: string;
  action: string;
  /** Optional Magic Templates category id (WP AI Restyle handoff). */
  categoryId?: string;
  ts: number;
}

const LATEST_RECORD_ID = 'latest' as const

/** Query param on the new editor tab URL — must match the ImageDB record id from `saveIndexedImage`. */
export const INDEXED_IMAGE_TRANSFER_QUERY_PARAM = 'indexedImageTransfer'
export const OPEN_IN_EDITOR_SOURCE_PROJECT_QUERY_PARAM = 'openInEditorSourceProjectId'
export const OPEN_IN_EDITOR_SOURCE_PROJECT_SESSION_KEY = 'openInEditorSourceProjectId'
export const INDEXED_IMAGE_ACTIONS = {
  REMOVE_BG: 'remove_bg',
  MAGIC_BG: 'magic_bg',
  AI_RESTYLE: 'ai_restyle',
  OPEN_EDITOR_AI_FASHION_MODELS: 'open_editor_ai_fashion_models',
  OPEN_EDITOR_PHOTOSHOOT: 'open_editor_photoshoot',
  OPEN_EDITOR_BATCH: 'open_editor_batch',
} as const

export type IndexedImageAction = (typeof INDEXED_IMAGE_ACTIONS)[keyof typeof INDEXED_IMAGE_ACTIONS]
export interface IndexedImage {
  url: string | null;
  action: string | null;
  categoryId?: string | null;
}

/* ---------------------------------------------------------------------- */
/*  Open helper (guarantees store exists)                                 */
/* ---------------------------------------------------------------------- */
async function openImageDB() {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(DB_STORE)) {
        db.createObjectStore(DB_STORE, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(PHOTOSHOOT_TRANSFERS_STORE)) {
        db.createObjectStore(PHOTOSHOOT_TRANSFERS_STORE, { keyPath: 'id' });
      }
    }
  });
}

/**
 * @param recordId - When omitted, overwrites the shared `latest` slot (e.g. bridge.html remove_bg).
 *   Pass a unique id for tab-scoped transfers so concurrent opens do not overwrite each other.
 * @param categoryId - Optional Magic Templates category id for AI Restyle handoff.
 */
export async function saveIndexedImage(
  blob: Blob,
  action: string,
  recordId?: string,
  categoryId?: string,
): Promise<void> {
  const db = await openImageDB();
  const id = recordId ?? LATEST_RECORD_ID;
  const storable = await toStorableBlobPayload(blob)

  const record: IndexedImageRecord = {
    id,
    blob: storable.value,
    blobType: storable.type,
    action,
    ts: Date.now(),
  }
  if (categoryId) {
    record.categoryId = categoryId
  }

  await db.put(DB_STORE, record);

  await db.close();
}

/* ---------------------------------------------------------------------- */
/*  Load the image                                                        */
/* ---------------------------------------------------------------------- */
/**
 * @param recordId - When omitted, reads the shared `latest` record (bridge / legacy).
 */
export async function loadIndexedImage(recordId?: string): Promise<IndexedImage | null> {
  const db = await openImageDB();
  const key = recordId ?? LATEST_RECORD_ID;
  const rec = (await db.get(DB_STORE, key)) as IndexedImageRecord | undefined;
  await db.close();

  if (!rec) return null;
  const blob = toReadableBlob(rec.blob, rec.blobType)
  if (!blob) return null

  return {
    url: URL.createObjectURL(blob),
    action: rec.action,
    categoryId: rec.categoryId ?? null,
  };
}

/* ---------------------------------------------------------------------- */
/*  Delete the current image                                              */
/* ---------------------------------------------------------------------- */
export async function deleteIndexedImage(recordId?: string): Promise<void> {
  const db = await openImageDB();
  const key = recordId ?? LATEST_RECORD_ID;
  await db.delete(DB_STORE, key);
  await db.close();
}

/* ---------------------------------------------------------------------- */
/*  Batch refine result transfer (editor -> batch)                        */
/* ---------------------------------------------------------------------- */
/**
 * Reuses the existing `images` store (no schema bump) with a distinct key prefix
 * so the refined cutout the editor produces can be picked up by the batch tab.
 */
const BATCH_REFINE_RESULT_PREFIX = 'batchRefineResult:'
/**
 * Owner-guarded pickup avoids cross-tab result theft, but it also means orphaned
 * refine results (e.g. owner tab closed before pickup) may never be claimed.
 * Bound storage growth by pruning old records opportunistically when scanning.
 */
const BATCH_REFINE_RESULT_MAX_AGE_MS = 24 * 60 * 60 * 1000

export function batchRefineResultKey(itemId: string): string {
  return `${BATCH_REFINE_RESULT_PREFIX}${itemId}`
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(blob)
  })
}

/** Editor side: persist the refined cutout keyed by the batch item id. */
export async function saveBatchRefineResult(itemId: string, blob: Blob): Promise<void> {
  await saveIndexedImage(blob, INDEXED_IMAGE_ACTIONS.OPEN_EDITOR_BATCH, batchRefineResultKey(itemId))
}

/**
 * Batch side: read the refined cutout as a data URL (Redux stores data URLs for
 * cutouts, so we materialize the blob into a string rather than a transient
 * object URL that could be revoked on reload).
 */
export async function readBatchRefineResultDataUrl(itemId: string): Promise<string | null> {
  const db = await openImageDB()
  const rec = (await db.get(DB_STORE, batchRefineResultKey(itemId))) as IndexedImageRecord | undefined
  await db.close()
  if (!rec) return null
  const blob = toReadableBlob(rec.blob, rec.blobType)
  if (!blob) return null
  return blobToDataUrl(blob)
}

export async function deleteBatchRefineResult(itemId: string): Promise<void> {
  await deleteIndexedImage(batchRefineResultKey(itemId))
}

/** Batch side fallback: discover any pending refine results without a known id. */
export async function listPendingBatchRefineResultIds(): Promise<string[]> {
  const db = await openImageDB()
  try {
    const keys = await db.getAllKeys(DB_STORE)
    const now = Date.now()
    const ids: string[] = []

    for (const key of keys) {
      if (typeof key !== 'string' || !key.startsWith(BATCH_REFINE_RESULT_PREFIX)) continue

      // Fetch only matching records; avoid materializing every blob in the store.
      const record = (await db.get(DB_STORE, key)) as IndexedImageRecord | undefined
      const ageMs = typeof record?.ts === 'number' ? now - record.ts : Number.POSITIVE_INFINITY
      if (ageMs > BATCH_REFINE_RESULT_MAX_AGE_MS) {
        // Stale orphan (owner tab likely gone) - reclaim storage.
        await db.delete(DB_STORE, key)
        continue
      }

      ids.push(key.slice(BATCH_REFINE_RESULT_PREFIX.length))
    }

    return ids
  } finally {
    await db.close()
  }
}
